import type { TicketHistoryEntry, TicketReply } from "@/lib/support";

const GENERIC_STATUS_NOTES = new Set([
  "vom melder wieder geöffnet.",
  "vom melder als gelöst markiert.",
  "vom melder als gelöst bestätigt.",
]);

const REOPEN_FROM = new Set(["resolved", "closed", "waiting"]);

export type SubjectUpdate = {
  text: string;
  actor: string;
  at: string;
  label: "Wieder geöffnet" | "Letzte Rückmeldung";
};

export function normalizeTicketText(value: string | null | undefined): string {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function isGenericNote(note: string): boolean {
  const normalized = normalizeTicketText(note).toLowerCase();
  return !normalized || GENERIC_STATUS_NOTES.has(normalized);
}

function eventTime(iso: string | null | undefined): number {
  const time = new Date(iso || "").getTime();
  return Number.isFinite(time) ? time : 0;
}

/**
 * Der Text, der beim Öffnen sofort sichtbar sein soll: die letzte sachliche
 * Rückmeldung des Melders, insbesondere der Grund für ein Wiederöffnen.
 * Die ursprüngliche Meldung bleibt daneben stehen und wird hier nicht wiederholt.
 */
export function selectCurrentSubject(input: {
  original?: string | null;
  reporterLogin?: string | null;
  history?: TicketHistoryEntry[] | null;
  replies?: TicketReply[] | null;
}): SubjectUpdate | null {
  const originalNorm = normalizeTicketText(input.original).toLowerCase();
  const reporter = String(input.reporterLogin || "").trim().toLowerCase();

  type Candidate = SubjectUpdate & { time: number };
  const candidates: Candidate[] = [];

  for (const entry of input.history || []) {
    const note = String(entry.note || "").trim();
    if (isGenericNote(note)) continue;
    const oldStatus = String(entry.old_status || "").toLowerCase();
    const newStatus = String(entry.new_status || "").toLowerCase();
    const reopened = newStatus === "open" && REOPEN_FROM.has(oldStatus);
    const author = String(entry.changed_by_login || "").trim().toLowerCase();
    const fromReporter = Boolean(reporter) && author === reporter;
    if (!reopened && !fromReporter) continue;
    if (!reopened && normalizeTicketText(note).length < 12) continue;
    candidates.push({
      text: note,
      actor: entry.changed_by_display || entry.changed_by_login || "Melder",
      at: entry.changed_at || "",
      label: reopened ? "Wieder geöffnet" : "Letzte Rückmeldung",
      time: eventTime(entry.changed_at),
    });
  }

  for (const reply of input.replies || []) {
    const text = String(reply.message || "").trim();
    if (!text) continue;
    const author = String(reply.author_login || "").trim().toLowerCase();
    if (reporter && author && author !== reporter) continue;
    if (!reporter && author) continue;
    candidates.push({
      text,
      actor: reply.author_display || reply.author_login || "Melder",
      at: reply.created_at || "",
      label: "Letzte Rückmeldung",
      time: eventTime(reply.created_at),
    });
  }

  candidates.sort((a, b) => b.time - a.time);
  const latest = candidates[0];
  if (!latest) return null;
  if (normalizeTicketText(latest.text).toLowerCase() === originalNorm) return null;

  const latestNorm = normalizeTicketText(latest.text).toLowerCase();
  const reopenTwin = candidates.find(
    (candidate) =>
      candidate.label === "Wieder geöffnet" &&
      normalizeTicketText(candidate.text).toLowerCase() === latestNorm &&
      Math.abs(candidate.time - latest.time) < 120_000,
  );

  const chosen = reopenTwin ?? latest;
  return {
    text: chosen.text,
    actor: chosen.actor,
    at: chosen.at,
    label: chosen.label,
  };
}

/** Status-Notiz, die zeitnah wortgleich als Antwort gespeichert wurde. */
export function statusNoteDuplicatesReply(
  note: string | null | undefined,
  changedAt: string | null | undefined,
  replies: TicketReply[] | null | undefined,
): boolean {
  const normalized = normalizeTicketText(note).toLowerCase();
  if (!normalized || isGenericNote(note || "")) return false;
  const changedTime = eventTime(changedAt);
  return (replies || []).some((reply) => {
    if (normalizeTicketText(reply.message).toLowerCase() !== normalized) return false;
    const replyTime = eventTime(reply.created_at);
    if (!changedTime || !replyTime) return true;
    return Math.abs(replyTime - changedTime) < 5 * 60 * 1000;
  });
}
